import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArrowLeft, Bell, AlertTriangle, Trash2 } from "lucide-react";

const KIND_META = {
  ADMIN_NOTICE: { label: "stilltouched-Team", color: "bg-blue-50 border-blue-200 text-blue-800" },
  ACCOUNT_NOTICE: { label: "Konto-Hinweis", color: "bg-red-50 border-red-200 text-red-800" },
  REPORT_OUTCOME: { label: "Meldungs-Ergebnis", color: "bg-amber-50 border-amber-200 text-amber-800" },
  PLATFORM_ANNOUNCEMENT: { label: "Ankündigung", color: "bg-purple-50 border-purple-200 text-purple-800" },
  UPDATE: { label: "Update", color: "bg-green-50 border-green-200 text-green-800" },
};

export default function SystemMessageDetailPage() {
  const { t } = useTranslation();
  const { messageId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(t("system_message.confirm_remove"))) return;
    setDeleting(true);
    try {
      await api.delete(`/messages/system/${messageId}`);
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
      navigate("/messages?tab=system", { replace: true });
    } catch {
      setError(t("system_message.delete_failed"));
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        // mark as read
        await api.post(`/messages/system/${messageId}/read`).catch(() => {});
        // fetch list (no detail endpoint – simple lookup from list)
        const { data } = await api.get("/messages/system");
        const found = (data.items || []).find((m) => m.message_id === messageId);
        if (!found) {
          setError(t("system_message.not_found"));
        } else {
          setMsg(found);
        }
        // Trigger unread-badge refresh
        try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
      } catch (e) {
        setError(t("system_message.load_error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user, navigate, messageId]);

  const meta = msg ? (KIND_META[msg.kind] || { label: "Nachricht", color: "bg-gray-50 border-gray-200 text-gray-800" }) : null;

  return (
    <div data-testid="system-message-detail" className="flex flex-col min-h-[calc(100vh-7.5rem)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] shrink-0">
        <button data-testid="system-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <Bell size={16} className="text-[#666]" />
          <p className="text-sm font-bold text-[#0A0A0A]">Systemnachricht</p>
        </div>
        {msg && (
          <button
            data-testid="system-msg-detail-delete"
            onClick={handleDelete}
            disabled={deleting}
            className="p-2 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
            aria-label={t("system_message.delete_msg")}
            title={t("system_message.delete_msg")}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="flex-1 px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
          </div>
        ) : error ? (
          <InfoCard variant="danger">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </InfoCard>
        ) : msg ? (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-2 py-0.5 text-[10px] font-medium border ${meta.color}`}>{meta.label}</span>
              <span className="text-[10px] text-[#999]">
                {new Date(msg.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
              </span>
            </div>
            {msg.subject && (
              <h2 className="text-base font-bold text-[#0A0A0A] mb-3">{msg.subject}</h2>
            )}
            <div className="bg-white border border-[#E5E5E5] p-4">
              <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{msg.body}</p>
            </div>
            <div className="mt-4 p-3 bg-[#FAFAFA] border-l-2 border-[#999]">
              <p className="text-[11px] text-[#666] leading-relaxed">
                Diese Nachricht stammt vom stilltouched-Team. Antworten in diesem Kanal sind nicht möglich.
                Bei Fragen oder Klärungsbedarf kontaktiere bitte den Support.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
